import { clsx } from 'clsx';
import { useCallback, useMemo } from 'react';
import { Checkbox, DropdownItem, FILTER_ITEM_TYPES, FilterItem } from '@treezor/design-system';

import { RenderSelectedValues } from '@/ui-kit/table-filters/RenderSelectedValues';
import { ICheckField, ICheckOption } from '@/ui-kit/table-filters/filters.type';

export interface ICheckFilterProps {
  field: ICheckField;
  label?: string;
  isFilled?: boolean;
  className?: string;
}

function isGroup(option: ICheckOption): boolean {
  return option?.children?.length > 0;
}

const getOptionValues = (options: ICheckOption[]): (string | number)[] => {
  return options.map((value) => (Number.isNaN(Number(value.value)) ? value.value : Number(value.value)));
};

export function CheckFilter({ field, label, isFilled = true, className }: ICheckFilterProps) {
  const selectedValuesSet = useMemo(() => new Set(field.values?.map((v) => v.value) ?? []), [field.values]);

  const onOptionClick = useCallback(
    (option: ICheckOption) => {
      const isSelected = selectedValuesSet.has(option.value);
      let newSelectedOptions: ICheckOption[] = [];

      if (isSelected) {
        newSelectedOptions = field.values?.filter((v) => v.value !== option.value) ?? [];
      } else {
        newSelectedOptions = [...(field.values ?? []), option];
      }

      field.handleChange(getOptionValues(newSelectedOptions));
    },
    [field, selectedValuesSet],
  );

  const onGroupClick = useCallback(
    (group: ICheckOption, isAllSelected: boolean) => {
      const allChildrenValues = new Set(group.children.map((c) => c.value));
      const otherSelectedOptions = field.values?.filter((v) => !allChildrenValues.has(v.value)) ?? [];

      let newSelectedOptions: ICheckOption[] = [];
      if (isAllSelected) {
        newSelectedOptions = otherSelectedOptions;
      } else {
        newSelectedOptions = [...otherSelectedOptions, ...group.children];
      }

      field.handleChange(getOptionValues(newSelectedOptions));
    },
    [field],
  );

  return (
    <FilterItem
      type={FILTER_ITEM_TYPES.CUSTOM}
      id="check"
      renderSelectedValues={(closeDropDown) => (
        <RenderSelectedValues
          label={label}
          values={!!field?.values?.length ? field.values.map((value) => value.label) : undefined}
          onReset={
            isFilled
              ? () => {
                  closeDropDown();
                  field.handleChange(null);
                }
              : undefined
          }
        />
      )}
      dropdownContent={() =>
        field.options?.map((option) => {
          if (isGroup(option)) {
            const allChildrenValues = new Set(option.children.map((c) => c.value));
            const selectedChildrenInGroup = field.values?.filter((v) => allChildrenValues.has(v.value)) ?? [];

            const isAllSelected = selectedChildrenInGroup.length === option.children.length;
            const isIndeterminate = selectedChildrenInGroup.length > 0 && !isAllSelected;

            return (
              <div
                className={clsx('border-y border-primary', option?.isDisabled ? 'pointer-events-none cursor-auto' : '')}
                key={`dropdown-item-${option.value}`}
              >
                <DropdownItem
                  label={option.label}
                  testId={`dropdown-item-${option.value}`}
                  onClick={() => !option?.isDisabled && onGroupClick(option, isAllSelected)}
                  prefix={
                    <Checkbox indeterminate={isIndeterminate} checked={isAllSelected} disabled={option?.isDisabled} />
                  }
                />

                <div className="flex flex-col [&>div]:pl-8">
                  {option.children.map((childOption) => (
                    <DropdownItem
                      label={childOption.label}
                      testId={`dropdown-item-${childOption.value}`}
                      onClick={() => !childOption?.isDisabled && onOptionClick(childOption)}
                      prefix={
                        <Checkbox
                          checked={selectedValuesSet.has(childOption.value)}
                          disabled={childOption?.isDisabled}
                        />
                      }
                      key={`dropdown-item-${childOption.value}`}
                    />
                  ))}
                </div>
              </div>
            );
          }

          return (
            <div
              className={option?.isDisabled ? 'pointer-events-none cursor-auto' : ''}
              key={`dropdown-item-${option.value}`}
            >
              <DropdownItem
                label={option.label}
                testId={`dropdown-item-${option.value}`}
                onClick={() => !option?.isDisabled && onOptionClick(option)}
                prefix={<Checkbox checked={selectedValuesSet.has(option.value)} disabled={option?.isDisabled} />}
              />
            </div>
          );
        })
      }
      className={className}
    />
  );
}
